/**
 * **번호 칸의 커서 자리 한 곳** — 의존성 없는 순수 모듈이다(Vue·Element·`@/` 별칭 금지 — 단위 시험이 node 에서 그대로 읽는다).
 *
 * 전화·사업자번호 칸은 치는 대로 하이픈을 다시 끼워 그린다(`telNumber.telFieldText`·`bizNumber.bizNoFieldText`). 다시 그리면 브라우저가
 * 커서를 끝으로 보내서, 가운데 숫자를 고치면 이어 친 숫자가 끝에 붙었다(2026-10-30 사용자 결정 「커서 자리를 지키는 장치를 넣는다」).
 * 그래서 커서 자리를 글자 위치가 아니라 **커서 앞 숫자 개수**로 기억했다가, 다시 그린 글자에서 같은 개수의 숫자 뒤로 옮긴다
 * (하이픈이 늘거나 줄어도 친 자리가 그대로다). 칸에 붙이는 일은 `directives/keepNumberCaret` 가 맡는다.
 */

const isDigit = (ch) => ch >= '0' && ch <= '9'

/** 글자의 숫자 개수 */
const digitCount = (text) => Array.from(text).filter(isDigit).length

/**
 * 숫자를 [count] 개 지난 바로 뒤의 글자 위치 — 0 이면 맨 앞, 숫자가 모자라면 글자 끝.
 *
 * @param {string} text 칸에 그려진 글자
 * @param {number} count 커서 앞에 둘 숫자 개수
 * @returns {number}
 */
export function caretAfterDigits(text, count) {
  if (count <= 0) return 0
  let seen = 0
  for (let index = 0; index < text.length; index++) {
    if (isDigit(text[index])) seen++
    if (seen === count) return index + 1
  }
  return text.length
}

/** [nth] 번째(0부터) 숫자의 글자 위치 — 없으면 -1 */
const indexOfDigit = (text, nth) => {
  let seen = -1
  for (let index = 0; index < text.length; index++) {
    if (isDigit(text[index]) && ++seen === nth) return index
  }
  return -1
}

/** 번호 모양 — 숫자와 구분 글자(하이픈·공백·괄호·점)뿐. 다른 글자가 섞인 값(예전 아이디 `S26…`·이메일)은 번호 칸 일이 아니다 */
const NUMBER_SHAPE = /^[0-9\s\-().]*$/

/**
 * **방금 고친 번호 칸의 글자와 커서 앞 숫자 개수** — 지우기가 하이픈 같은 구분 글자만 지웠으면(숫자 개수가 그대로) 그 옆 숫자를
 * 대신 지운다: Backspace 는 커서 앞 숫자, Delete 는 커서 뒤 숫자. 하이픈은 다시 끼워지므로 그대로 두면 지우기가 헛돈다. 고친 값이
 * 번호 모양이 아니면 손대지 않고 커서도 옮기지 않는다(`digitsBefore: null`). 순수.
 *
 * @param {{before: string, after: string, caret: number, inputType: ?string}} edit 고치기 전 글자·고친 뒤 글자·고친 뒤 커서·
 *   `InputEvent.inputType`
 * @returns {{text: string, digitsBefore: ?number}} 칸에 넘길 글자와, 다시 그린 뒤 커서 앞에 둘 숫자 개수(옮기지 않으면 null)
 */
export function editedNumber({ before, after, caret, inputType }) {
  if (!NUMBER_SHAPE.test(after)) return { text: after, digitsBefore: null }
  const digitsBefore = digitCount(after.slice(0, caret))
  const onlySeparatorRemoved = after.length < before.length && digitCount(after) === digitCount(before)

  if (onlySeparatorRemoved && inputType === 'deleteContentBackward' && digitsBefore > 0) {
    const index = indexOfDigit(after, digitsBefore - 1)
    return { text: after.slice(0, index) + after.slice(index + 1), digitsBefore: digitsBefore - 1 }
  }
  if (onlySeparatorRemoved && inputType === 'deleteContentForward') {
    const index = indexOfDigit(after, digitsBefore)
    if (index > -1) return { text: after.slice(0, index) + after.slice(index + 1), digitsBefore }
  }
  return { text: after, digitsBefore }
}
